/**
 * useAgentStream -- Streaming state machine hook for ADDAPT Agent chat.
 *
 * Manages the full lifecycle of an agent response (which may span
 * multiple model-invocation turns when the Strands agent loops
 * through tool calls):
 *
 *   idle -> thinking -> toolRunning -> streaming
 *        -> (agentEnd: turn boundary, loop back to thinking)
 *        -> complete  (only on final `message` event)
 *
 * Tool steps, streamed text, and thinking text ACCUMULATE across
 * turns so the UI shows one consolidated response.
 */

import { useState, useCallback, useRef } from 'react';
import type { WebSocketMessage } from '../services/websocket.service';
import type {
  AgentStreamState,
  AgentStreamPhase,
  ToolStep,
  ToolCallSummary,
  AgentMessagePart,
} from '../types/agent-messages';
import { getToolDisplayName } from '../types/agent-messages';
import { parseAgentMessage } from '../utils/parsers';

/** Initial (idle) state. */
const INITIAL_STATE: AgentStreamState = {
  phase: 'idle',
  thinkingText: '',
  toolSteps: [],
  streamedText: '',
  fullMessage: null,
  toolCalls: null,
  error: null,
};

export interface UseAgentStreamReturn {
  /** Current streaming state -- drive all UI from this. */
  state: AgentStreamState;

  /**
   * Feed a WebSocket message into the state machine.
   * Call this from the onmessage handler for every agent event type.
   * Returns `true` if the event was handled, `false` otherwise.
   */
  handleEvent: (data: WebSocketMessage) => boolean;

  /** Reset state to idle. Call before sending a new user message. */
  reset: () => void;

  /**
   * Build the finalized AgentMessagePart[] from the completed stream state.
   * Call once after phase reaches 'complete' to create the persisted message.
   */
  buildMessageParts: () => AgentMessagePart[];

  /**
   * Store backend-provided structuredParts for Path B forward-compat.
   */
  setStructuredParts: (parts: AgentMessagePart[]) => void;
}

export const useAgentStream = (): UseAgentStreamReturn => {
  const [state, setState] = useState<AgentStreamState>(INITIAL_STATE);

  const streamedTextRef = useRef('');
  const thinkingTextRef = useRef('');
  const toolStepsRef = useRef<ToolStep[]>([]);
  const turnStartTimeRef = useRef<number>(0);
  const structuredPartsRef = useRef<AgentMessagePart[] | undefined>(undefined);

  const reset = useCallback(() => {
    streamedTextRef.current = '';
    thinkingTextRef.current = '';
    toolStepsRef.current = [];
    turnStartTimeRef.current = 0;
    structuredPartsRef.current = undefined;
    setState(INITIAL_STATE);
  }, []);

  const setStructuredParts = useCallback((parts: AgentMessagePart[]) => {
    structuredPartsRef.current = parts;
  }, []);

  const handleEvent = useCallback((data: WebSocketMessage): boolean => {
    switch (data.type) {
      case 'agentStart': {
        // A new model-invocation turn is starting.
        // If this is the very first turn, record the start time.
        // Do NOT clear accumulated tool steps / streamed text -- the agent
        // may loop through multiple turns and we want one consolidated message.
        if (turnStartTimeRef.current === 0) {
          turnStartTimeRef.current = Date.now();
        }
        setState(prev => ({
          ...prev,
          phase: 'thinking',
        }));
        return true;
      }

      case 'thinking': {
        const text = data.text ?? '';
        thinkingTextRef.current += text;
        setState(prev => ({
          ...prev,
          phase: prev.phase === 'idle' ? 'thinking' : prev.phase,
          thinkingText: thinkingTextRef.current,
        }));
        return true;
      }

      case 'toolUse': {
        const newStep: ToolStep = {
          toolUseId: data.toolUseId ?? `tool-${Date.now()}`,
          toolName: data.toolName ?? 'unknown',
          operationName: data.operationName,
          displayName: getToolDisplayName(data.toolName ?? 'unknown', data.operationName),
          status: 'running',
          input: data.input,
          startTime: Date.now(),
        };
        toolStepsRef.current = [...toolStepsRef.current, newStep];
        setState(prev => ({
          ...prev,
          phase: 'toolRunning',
          toolSteps: toolStepsRef.current,
        }));
        return true;
      }

      case 'toolResult': {
        const id = data.toolUseId;
        toolStepsRef.current = toolStepsRef.current.map(step => {
          if (step.toolUseId !== id) return step;
          return {
            ...step,
            status: data.status === 'error' ? 'error' : 'complete',
            output: data.content,
            duration: Date.now() - step.startTime,
          } as ToolStep;
        });
        setState(prev => ({
          ...prev,
          toolSteps: toolStepsRef.current,
        }));
        return true;
      }

      case 'delta': {
        const text = data.text ?? '';
        streamedTextRef.current += text;
        setState(prev => ({
          ...prev,
          phase: 'streaming',
          streamedText: streamedTextRef.current,
        }));
        return true;
      }

      case 'agentEnd': {
        // End of one model-invocation turn. The agent may start another
        // turn (more tool calls), so do NOT set phase to 'complete'.
        // Stay in current phase -- the UI keeps showing the live message.
        // The final `message` event is what signals true completion.
        return true;
      }

      case 'message': {
        if (data.source !== 'agent') return false;
        setState(prev => ({
          ...prev,
          phase: 'complete',
          fullMessage: data.message ?? prev.streamedText,
          toolCalls: (data.toolCalls as ToolCallSummary[]) ?? null,
        }));
        return true;
      }

      case 'error': {
        setState(prev => ({
          ...prev,
          phase: 'error',
          error: data.message ?? 'An unknown error occurred',
        }));
        return true;
      }

      default:
        return false;
    }
  }, []);

  const buildMessageParts = useCallback((): AgentMessagePart[] => {
    const turnDuration = turnStartTimeRef.current
      ? (Date.now() - turnStartTimeRef.current) / 1000
      : undefined;

    return parseAgentMessage({
      text: streamedTextRef.current,
      toolSteps: [...toolStepsRef.current],
      toolCalls: state.toolCalls,
      fullMessage: state.fullMessage,
      turnDuration,
      structuredParts: structuredPartsRef.current,
    });
  }, [state.fullMessage, state.toolCalls]);

  return { state, handleEvent, reset, buildMessageParts, setStructuredParts };
};
